import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { Modal, Box, Typography, Button, useTheme, IconButton } from '@mui/material'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'
import DateFilter from 'components/DateFilter'
import { ActionIcon } from 'components/Modal/ActionModal'
import EmptyScreen from 'packages/ui/EmptyScreen'
import { parseTimestamp, formatTimeDisplay } from '../helpers'
import GraphTooltip from './Tooltip/GraphTooltip'

const OPERATION_GET_CONNECTED_DEVICES_GRAPH = gql`
  query GetConnectedDevicesGraph($sfIds: [String], $start: String, $end: String) {
    monitoringGraphConnectedDevices(sf_ids: $sfIds, start: $start, end: $end) {
      timestamps
      metrics {
        key
        values
      }
    }
  }
`

interface ConnectedDevicesModalProps {
  open: boolean
  onClose: () => void
  sfIds: string[]
}

interface GraphDataPoint {
  timestamp: string
  time: string
  guest: number
  private: number
  other: number
}

interface MetricData {
  key: string
  values: number[]
}

interface GraphResponse {
  monitoringGraphConnectedDevices: {
    timestamps: string[]
    metrics: MetricData[]
  }
}

const GUEST_COLOR = '#EC7A74'
const PRIVATE_COLOR = '#4F8AA4'
const OTHER_COLOR = '#A5D9CB'

const axisStyle = {
  fontSize: 10,
  fill: '#A2AAB8',
}

const ConnectedDevicesModal: React.FC<ConnectedDevicesModalProps> = ({ open, onClose, sfIds }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [timeRangePreset, setTimeRangePreset] = useState<string>('-1d')
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  })
  const [isCustomRange, setIsCustomRange] = useState<boolean>(false)

  const getTimeRangeVariables = () => {
    if (isCustomRange && dateRange.start && dateRange.end) {
      return {
        start: Math.floor(dateRange.start.getTime() / 1000).toString(),
        end: Math.floor(dateRange.end.getTime() / 1000).toString()
      }
    }
    return {
      start: timeRangePreset,
      end: undefined
    }
  }

  const { loading, data } = useQuery<GraphResponse>(OPERATION_GET_CONNECTED_DEVICES_GRAPH, {
    variables: {
      sfIds,
      ...getTimeRangeVariables()
    },
    fetchPolicy: 'network-only',
    skip: !open
  })

  const handleTimeRangeChange = (newTimeRange: string) => {
    if (newTimeRange !== timeRangePreset || isCustomRange) {
      setTimeRangePreset(newTimeRange)
      setIsCustomRange(false)
      setDateRange({ start: null, end: null })
    }
  }

  const handleDateRangeSubmit = (start: Date | null, end: Date | null) => {
    if (start && end) {
      setDateRange({ start, end })
      setIsCustomRange(true)
      setTimeRangePreset('')
    }
  }

  const handleDateRangeReset = () => {
    setDateRange({ start: null, end: null })
    setIsCustomRange(false)
    setTimeRangePreset('-1d')
  }

  const filterInitialZeroValues = (data: GraphDataPoint[]): GraphDataPoint[] => {
    if (data.length === 0) return []
    
    let firstNonZeroIndex = Math.max(0, data.length - 6)
    for (let i = 0; i < data.length; i++) {
      const hasNonZeroValue = data[i].guest > 0 || data[i].private > 0 || data[i].other > 0
      
      if (hasNonZeroValue) {
        firstNonZeroIndex = Math.max(0, i - 6)
        break
      }
    }
    
    return data.slice(firstNonZeroIndex)
  }

  const graphData = useMemo((): GraphDataPoint[] => {
    if (!data?.monitoringGraphConnectedDevices) return []
    
    const { timestamps, metrics } = data.monitoringGraphConnectedDevices
    
    const guestMetric = metrics.find((m: MetricData) => m.key === 'active_portal_users')
    const privateMetric = metrics.find((m: MetricData) => m.key === 'active_ran_users')
    const otherMetrics = metrics.filter((m: MetricData) => 
      m.key === 'active_private_users' || 
      m.key === 'active_custom_users' || 
      m.key === 'active_users_second_class'
    )

    const rawData = timestamps.map((timestamp: string, index: number): GraphDataPoint => {
      const date = parseTimestamp(timestamp)
      const otherTotal = otherMetrics.reduce((sum, metric) => sum + (metric.values[index] || 0), 0)
      
      return {
        timestamp,
        time: formatTimeDisplay(date),
        guest: guestMetric?.values[index] || 0,
        private: privateMetric?.values[index] || 0,
        other: otherTotal
      }
    })

    return filterInitialZeroValues(rawData)
  }, [data])

  const activeMetrics = useMemo(() => ({
    guest: graphData.some(d => d.guest > 0),
    private: graphData.some(d => d.private > 0),
    other: graphData.some(d => d.other > 0),
  }), [graphData])

  const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 1000,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '24px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none'
  }

  const buttonStyle = {
    borderRadius: '4px',
    padding: '4px 10px',
    minHeight: '28px',
    height: '28px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'white',
    fontWeight: 500,
    fontSize: '12px',
    color: theme.palette.grey[700],
    textTransform: 'none' as const,
    whiteSpace: 'nowrap' as const,
    '&:hover': {
      backgroundColor: '#F5F5F5',
    }
  }

  const activeButtonStyle = {
    ...buttonStyle,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: '#EDF6FF',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: '#E4F1FF',
    }
  }

  const isEmpty = !loading && graphData.length === 0

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ 
          p: 3, 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ActionIcon>
              <i className="fa-regular fa-circle-info" />
            </ActionIcon>
            <Typography variant="h6" component="h2">
              {t('kpis_widgets.kpis_network_informations.connected_devices_title')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {['-6h', '-1d', '-7d', '-30d'].map((range) => (
                <Button
                  key={range}
                  onClick={() => handleTimeRangeChange(range)}
                  sx={!isCustomRange && timeRangePreset === range ? activeButtonStyle : buttonStyle}
                >
                  {range === '-6h' && t('kpis_widgets.time_period.last_6_hours')}
                  {range === '-1d' && t('kpis_widgets.time_period.last_24_hours')}
                  {range === '-7d' && t('kpis_widgets.time_period.last_7_days')}
                  {range === '-30d' && t('kpis_widgets.time_period.last_30_days')}
                </Button>
              ))}
            </Box>
            <DateFilter
              placement='bottom-start'
              onReset={handleDateRangeReset}
              startDate={dateRange.start}
              endDate={dateRange.end}
              onStartDateChange={(value) => {
                setDateRange(prev => ({ ...prev, start: value }))
              }}
              onEndDateChange={(value) => {
                setDateRange(prev => ({ ...prev, end: value }))
              }}
              onSubmit={handleDateRangeSubmit}
              placeholderKey={'kpis_widgets.filters.date_range'}
              showPresets={true}
              blockFutureDates={true}
              inputSx={{
                border: `1px solid ${theme.palette.border.primary}`,
                borderRadius: '4px',
                height: '28px',
                minWidth: '235px',
                '& .MuiInputBase-root': {
                  height: '28px',
                },
                '& .MuiInputBase-input': {
                  padding: '4px 10px',
                  fontSize: '12px',
                }
              }}
            />
            <IconButton
              onClick={onClose}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: '#f5f5f5f2',
                '&:hover': {
                  backgroundColor: '#f0f0f0f2',
                } 
              }}
            >
              <i className="fa-regular fa-xmark" style={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ 
          flex: 1, 
          p: 3, 
          overflow: 'auto',
          minHeight: 400
        }}>
          {loading ? (
            <Box sx={{ height: 400, width: '100%' }}>
              <ChartSkeleton />
            </Box>
          ) : isEmpty ? (
            <Box sx={{ 
              height: 400, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <EmptyScreen icon="chart-line">
                {t('kpis_widgets.graphs.no_data')}
              </EmptyScreen>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={graphData}
                margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="time"
                  tick={axisStyle}
                  stroke="#E5E7EB"
                />
                <YAxis 
                  tick={axisStyle}
                  stroke="#E5E7EB"
                />
                <RechartsTooltip content={<GraphTooltip />} />
                <Legend
                  wrapperStyle={{ 
                    fontSize: 12
                  }}
                  iconType="circle"
                  iconSize={10}
                />
                {activeMetrics.guest && (
                  <Line
                    type="monotone"
                    dataKey="guest"
                    name={t('kpis_widgets.connected_devices.guest')}
                    stroke={GUEST_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {activeMetrics.private && (
                  <Line
                    type="monotone"
                    dataKey="private"
                    name={t('kpis_widgets.connected_devices.private')}
                    stroke={PRIVATE_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {activeMetrics.other && (
                  <Line
                    type="monotone"
                    dataKey="other"
                    name={t('kpis_widgets.connected_devices.other')}
                    stroke={OTHER_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default ConnectedDevicesModal